import React, { useCallback, type RefObject } from 'react';
import type { Place, ViewState, RowBoundary, PortBoundary } from '../types/warehouse.types';
import { findPlaceAtPosition } from '../utils/placeUtils';
import { screenToWorld } from '../utils/coordinateTransform';
import { lineToRectanglePolygon } from '../utils/geometry';
import type { BufferedCanvasCache } from '../renderers/core/BufferedCanvasCache';

const ZERO_PAN_SHIFT = { deltaX: 0, deltaY: 0 };

interface UseCanvasInteractionProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  places: Map<string, Place>;
  viewState: ViewState;
  rowBoundaries?: RowBoundary[];
  portBoundaries?: PortBoundary[];
  onRowSelect?: (row: RowBoundary | null) => void;
  onPlaceSelect?: (place: Place | null) => void;
  onPlaceMultiSelect?: (place: Place, ctrlKey: boolean) => void;
  onPortSelect?: (port: PortBoundary | null) => void;
  onPortMultiSelect?: (port: PortBoundary, ctrlKey: boolean) => void;
  onPlaceDoubleClick?: (place: Place) => void; // New callback for double-click
  onRowDoubleClick?: (row: RowBoundary) => void;
  didPan: () => boolean;
  isTouching?: boolean; // To prevent click detection during touch gestures
  bufferedCacheRef?: React.MutableRefObject<BufferedCanvasCache | null>;
}

interface UseCanvasInteractionResult {
  handleMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void;
}

export const useCanvasInteraction = ({
  canvasRef,
  places,
  viewState,
  rowBoundaries,
  portBoundaries,
  onRowSelect,
  onPlaceSelect,
  onPlaceMultiSelect,
  onPlaceDoubleClick,
  onRowDoubleClick,
  onPortSelect,
  onPortMultiSelect,
  didPan,
  isTouching,
  bufferedCacheRef
}: UseCanvasInteractionProps): UseCanvasInteractionResult => {
  // Check if a point is inside a polygon (ray casting algorithm)
  const isPointInPolygon = useCallback((point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersect = ((yi > point.y) !== (yj > point.y)) && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }, []);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Check for clicks (only if not dragging/panning/touching)
    if (isTouching || didPan()) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const panShift = bufferedCacheRef?.current?.getPanShift() || ZERO_PAN_SHIFT;
    const correctedX = x - panShift.deltaX;
    const correctedY = y - panShift.deltaY;

    const worldPos = screenToWorld(x, y, viewState, canvas.width, canvas.height);

    // Priority 1: Check for place selection
    const clickedPlace = findPlaceAtPosition(places, correctedX, correctedY);

    if (clickedPlace) {
      // Check if Ctrl key is pressed for multi-selection
      if (e.ctrlKey || e.metaKey) {
        onPlaceMultiSelect?.(clickedPlace, true);
      } else {
        onPlaceSelect?.(clickedPlace);
      }
      return;
    }

    // Priority 2: Check for port selection
    if (onPortSelect && portBoundaries) {
      let clickedPort: PortBoundary | null = null;
      const portWidth = 15 / viewState.scale; // Adaptive width based on zoom

      for (const port of portBoundaries) {
        if (port.polygon.length === 2) {
          if (!port.hitBox) {
          // Port is a line - convert to rectangle
            port.hitBox = lineToRectanglePolygon(
              port.polygon[0],
              port.polygon[1],
              portWidth
            );
          }
          
          if (port.hitBox.length > 0 && isPointInPolygon(worldPos, port.hitBox)) {
            clickedPort = port;
            break;
          }
        } else if (port.polygon.length >= 3) {
          // Port is already a polygon
          if (isPointInPolygon(worldPos, port.polygon)) {
            clickedPort = port;
            break;
          }
        }
      }

      if (clickedPort) {
        // Check if Ctrl key is pressed for multi-selection
        if (e.ctrlKey || e.metaKey) {
          onPortMultiSelect?.(clickedPort, true);
        } else {
          onPortSelect?.(clickedPort);
        }
        return;
      }
    }

    // Priority 3: Check for row selection (or click outside to deselect)
    if (onRowSelect) {
      // Ignore row selection if Ctrl key is pressed (only for places/ports multi-select)
      if (e.ctrlKey || e.metaKey) {
        return;
      }
      
      let clickedRow: RowBoundary | null = null;
      if (rowBoundaries) {
        for (const row of rowBoundaries) {
          if (isPointInPolygon(worldPos, row.polygon)) {
            clickedRow = row;
            break;
          }
        }
      }
      // If click didn't hit a row, place or port and it was a left-click
      // then deselect any selected places or ports.
      if (!clickedRow) {
        if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
          onPlaceSelect?.(null);
          onPortSelect?.(null);
        }
      }

      onRowSelect(clickedRow);
    }
  }, [
    isTouching,
    didPan,
    places,
    canvasRef,
    viewState,
    rowBoundaries,
    portBoundaries,
    onRowSelect,
    onPlaceSelect,
    onPlaceMultiSelect,
    onPortSelect,
    onPortMultiSelect,
    isPointInPolygon,
    bufferedCacheRef,
  ]);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const panShift = bufferedCacheRef?.current?.getPanShift() || ZERO_PAN_SHIFT;
    const correctedX = x - panShift.deltaX;
    const correctedY = y - panShift.deltaY;

    const clickedPlace = findPlaceAtPosition(places, correctedX, correctedY);

    if (clickedPlace && onPlaceDoubleClick) {
      onPlaceDoubleClick(clickedPlace);
      return;
    }

    // Check for row at double-click position
    if (onRowDoubleClick && rowBoundaries) {
      // Convert screen coordinates to world coordinates
      const worldPos = screenToWorld(x, y, viewState, canvas.width, canvas.height);

      for (const row of rowBoundaries) {
        if (isPointInPolygon(worldPos, row.polygon)) {
          onRowDoubleClick(row);
          return;
        }
      }
    }
  }, [places, canvasRef, viewState, rowBoundaries, onPlaceDoubleClick, onRowDoubleClick, isPointInPolygon, bufferedCacheRef]);

  return {
    handleMouseUp,
    handleDoubleClick
  };
};